import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Switch, Tooltip } from '@mui/material';
import { DarkMode as DarkModeIcon, LightMode as LightModeIcon } from '@mui/icons-material';
import useThemeMode from 'hooks/useThemeMode';

/**
 * Toggle de tema del footer del sidebar: sol · switch · luna (estilo referencia).
 * El switch conserva el acento dorado (microacento permitido). En rail colapsado
 * se reduce a un IconButton sol/luna.
 */
function SidebarThemeToggle({ iconOnly = false }) {
  const { t } = useTranslation(['buttons']);
  const { isDark, toggle } = useThemeMode();

  const label = isDark
    ? t('switchToLightMode', { ns: 'buttons', defaultValue: 'Activar modo claro' })
    : t('switchToDarkMode', { ns: 'buttons', defaultValue: 'Activar modo oscuro' });

  if (iconOnly) {
    return (
      <Tooltip title={label} placement="right">
        <IconButton
          aria-label={label}
          color="menuItem"
          data-tour="theme-toggle-sidebar"
          onClick={toggle}
          sx={{ alignSelf: 'center' }}>
          {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box
      data-tour="theme-toggle-sidebar"
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 1.5,
        justifyContent: 'center',
        minHeight: 48
      }}>
      <LightModeIcon
        fontSize="small"
        sx={{ color: isDark ? 'text.secondary' : 'text.primary' }}
      />
      <Switch
        checked={isDark}
        inputProps={{ 'aria-label': label }}
        onChange={toggle}
        size="small"
      />
      <DarkModeIcon
        fontSize="small"
        sx={{ color: isDark ? 'text.primary' : 'text.secondary' }}
      />
    </Box>
  );
}

SidebarThemeToggle.propTypes = {
  iconOnly: PropTypes.bool
};

export default SidebarThemeToggle;
